import type { infrastructurePortalUtil } from 'generated-ts-common';
import { intl } from '../../../../lib';

type GenCode = infrastructurePortalUtil.saf.infrastructure.model.svs.GenCode;
export const otherValueIndicator = 'OTHER___';

export type GenCodeValueTypes =
  | string
  | string[]
  | GenCode
  | GenCode[]
  | null
  | undefined;

export function isGenCodeArray(value: GenCodeValueTypes): value is GenCode[] {
  if (
    !value ||
    typeof value === 'string' ||
    !Array.isArray(value) ||
    isStringArray(value)
  ) {
    return false;
  }

  return (value as GenCode[]).every(isGenCode);
}

export function isGenCode(value: GenCodeValueTypes): value is GenCode {
  if (!value || typeof value === 'string' || Array.isArray(value)) {
    return false;
  }
  return 'valueMappedAsHL7' in value;
}

export function isStringArray(value: unknown[] | unknown): value is string[] {
  return Array.isArray(value) && value.every(item => typeof item === 'string');
}

export function createOtherOption(): GenCode {
  return {
    value: 'OTHER',
    codingScheme: 'OTHER',
    displayName: intl('common.other'),
    valueMappedAsHL7: otherValueIndicator
  };
}

export function hasOtherOption(items: GenCode[]): boolean {
  return items.some(isOtherOptionGenCode);
}

export function getOtherOptionText(items: GenCode[]): string {
  const otherOption = items.find(isOtherOptionGenCode);
  if (!otherOption) {
    return '';
  }

  return otherOption.valueMappedAsHL7.substring(otherValueIndicator.length);
}

export function updateOtherOptionValue(
  items: GenCode[],
  newValue: string
): GenCode[] {
  const otherOption = items.find(isOtherOptionGenCode);

  if (!otherOption) {
    const newOption = createOtherOption();
    newOption.valueMappedAsHL7 = otherValueIndicator + newValue;
    return [...items, newOption];
  }

  return items.map(item => {
    if (isOtherOptionValue(item.valueMappedAsHL7)) {
      return {
        ...item,
        valueMappedAsHL7: otherValueIndicator + newValue
      };
    }
    return item;
  });
}

export function isOtherOptionGenCode(value: GenCode): boolean {
  return isOtherOptionValue(value.valueMappedAsHL7);
}

function isOtherOptionValue(value: string): boolean {
  return value.startsWith(otherValueIndicator);
}

/**
 * Checks if a string matches the HL7 format used for valueMappedAsHL7.
 * HL7 strings typically contain '^^^&' and '&ISO' as delimiters.
 * Example: '1.1.1.1.1^^^&eHealth-Solutions-Stroke-Anticoagulants&ISO'
 * @param value - The string to check
 * @returns {boolean} True if the string matches HL7 format, false otherwise
 */
export function isValueHL7String(value: string): boolean {
  const trimmed = value.trim();
  const separator = '^^^&';
  const suffix = '&ISO';

  const separatorIndex = trimmed.indexOf(separator);

  // no separator or separator is at the start
  if (separatorIndex <= 0) {
    return false;
  }

  // suffix must be at the end
  if (!trimmed.endsWith(suffix)) {
    return false;
  }

  // there must be some content between the separator and the suffix
  const contentBetween = trimmed.substring(
    separatorIndex + separator.length,
    trimmed.length - suffix.length
  );
  return contentBetween.length > 0;
}
/*
 * Checks if all strings in the provided array match the HL7 format.
 */
export function areAllValuesHL7String(values: string[]): boolean {
  return values.every(isValueHL7String);
}

/**
 * Returns a predicate function to find a GenCode item by matching the provided value
 * against both valueMappedAsHL7 and value properties, including a default value fallback.
 * @param value - The primary value to match
 * @param defaultValue - The default value to match if the primary does not match
 * @returns A predicate function that takes a GenCode item and returns true if it matches
 */
export function findItem(value: string, defaultValue: string) {
  return (item: GenCode) =>
    item.valueMappedAsHL7 === value ||
    item.value === value ||
    item.valueMappedAsHL7 === defaultValue ||
    item.value === defaultValue;
}
